import { useEffect, useState } from 'react';

import { cn, iaRa } from 'lib/helpers/utility';

import DocumentSheet from './DocumentSheet';
import { sheetMetrics } from './lib/docStyles';
import { useDocumentZoom } from './lib/documentZoomContext';
import { sliceTableGroups, tableUnits } from './lib/paginate';
import DocumentZoomToggle from './components/DocumentZoomToggle';
import DocumentMeasureTree from './components/DocumentMeasureTree';
import { useDocumentPagesLayout } from './lib/useDocumentPagesLayout';

const renderBlock = (block, slot) => {
  if (!block.table) return block.node;

  const units = tableUnits(block.table.groups);
  const groups = slot?.units
    ? sliceTableGroups({ groups: block.table.groups, units, from: slot.from, to: slot.to })
    : block.table.groups;

  const leading = slot?.continued ? block.continuedLeading : block.leading;

  return (
    <>
      {leading || null}
      {block.render({ ...block.table, groups })}
    </>
  );
};

/**
 * @param {object} params
 * @param {Array<object>} params.blocks - `{ key, node }`, or a table block
 *   `{ key, leading, continuedLeading, table: { columns, groups, ...props }, render }`.
 * @param {(pageIndex: number) => React.ReactNode} params.renderHeader
 * @param {(params: {page: number, pageCount: number}) => React.ReactNode} params.renderFooter
 * @param {boolean} [params.fluid] - Render every block on one growing sheet
 *   rather than paginating, for a screen that is not imitating paper.
 * @param {boolean} [params.fullWidth] - With `fluid`, drop the sheet's max
 *   width so the document fills its container.
 * @param {number} [params.zoom] - With `fluid`, scale the whole document's
 *   type. `zoom` reflows rather than transforming, so the sheet keeps filling
 *   its container.
 * @param {boolean} [params.fillHeight] - With `fluid`, grow the sheet to its
 *   container's height and keep the footer on the bottom edge, so a short
 *   invoice does not leave the fine print floating mid-page. The container has
 *   to be a flex column with a height of its own — inside a virtualized row,
 *   whose height is whatever its content measures, there is nothing to fill.
 */
const DocumentPages = ({
  blocks = [],
  renderHeader,
  renderFooter,
  className,
  sheetClassName,
  landscape = false,
  fluid = false,
  fullWidth = false,
  fillHeight = false,
  zoom: textZoom
}) => {
  const items = iaRa(blocks).filter(Boolean);
  const { sheetWidth } = sheetMetrics(landscape);

  const zoom = useDocumentZoom();
  const setCanZoom = zoom?.setCanZoom;

  const [localActualSize, setLocalActualSize] = useState(false);

  const { layout, fitScale, stackHeight, wrapperRef, stackRef, measureRef, setNodeRef } =
    useDocumentPagesLayout(items, { landscape });

  const canZoom = fitScale < 1;

  useEffect(() => setCanZoom?.(canZoom), [canZoom, setCanZoom]);

  const pages = layout?.pages || [items.map((block) => ({ key: block.key }))];
  const pageCount = pages.length;

  const localZoom = {
    canZoom,
    isActualSize: localActualSize,
    toggleActualSize: () => setLocalActualSize((previous) => !previous)
  };

  const isActualSize = zoom ? zoom.isActualSize : localActualSize;
  const scale = canZoom && !isActualSize ? fitScale : 1;

  if (fluid) {
    return (
      <div className={cn('w-full', fillHeight && 'flex h-full flex-1 flex-col', className)}>
        {landscape && <style>{'@page { size: letter landscape; }'}</style>}

        <DocumentSheet
          fluid
          fullWidth={fullWidth}
          landscape={landscape}
          style={textZoom ? { zoom: textZoom } : undefined}
          className={cn(fillHeight && 'flex flex-1 flex-col', sheetClassName)}
          data-page={1}>
          {renderHeader?.(0)}
          <div className={cn(fillHeight && 'flex-1')}>
            {items.map((block) => (
              <div className="flow-root" key={block.key}>
                {renderBlock(block, null)}
              </div>
            ))}
          </div>
          <div className="mt-6">{renderFooter?.({ page: 1, pageCount: 1 })}</div>
        </DocumentSheet>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className={cn('w-full', className)}>
      {landscape && <style>{'@page { size: letter landscape; }'}</style>}

      <DocumentMeasureTree
        items={items}
        renderHeader={renderHeader}
        renderFooter={renderFooter}
        setNodeRef={setNodeRef}
        measureRef={measureRef}
        landscape={landscape}
      />

      {!zoom && canZoom && (
        <div className="sticky top-0 z-10 mb-2 flex justify-end print:hidden">
          <DocumentZoomToggle zoom={localZoom} />
        </div>
      )}

      <div className={cn('w-full', canZoom && 'overflow-x-auto')}>
        <div
          style={
            canZoom
              ? { width: sheetWidth * scale, height: stackHeight * scale || undefined }
              : undefined
          }
          className="print:!h-auto print:!w-auto">
          <div
            ref={stackRef}
            className="flex flex-col items-center gap-6 print:!w-auto print:gap-0 print:![transform:none]"
            style={
              canZoom
                ? { transform: `scale(${scale})`, transformOrigin: 'top left', width: sheetWidth }
                : undefined
            }>
            {pages.map((slots, pageIndex) => (
              <DocumentSheet
                key={`page-${pageIndex}`}
                className={sheetClassName}
                landscape={landscape}
                data-page={pageIndex + 1}>
                <div className="flex h-full flex-col">
                  {renderHeader?.(pageIndex)}
                  <div className="min-h-0 flex-1">
                    {slots.map((slot) => {
                      const block = items.find((item) => item.key === slot.key);

                      if (!block) return null;

                      return (
                        <div className="flow-root" key={`${slot.key}-${slot.from ?? 0}`}>
                          {renderBlock(block, { ...slot, units: slot.to != null })}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-auto">
                    {renderFooter?.({ page: pageIndex + 1, pageCount })}
                  </div>
                </div>
              </DocumentSheet>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentPages;
